import React from 'react';
import TooltipTrigger from 'react-popper-tooltip';

import { Global, css } from '@emotion/react';

import { styled } from '../theme';
import Text from './Text';

const globalStyles = css`
    .tooltip-container {
        display: flex;
        flex-direction: column;
        padding: 0.4rem;
        opacity: 0.8;
        max-height: 50vh;
        max-width: 350px;
        overflowy: overlay;
        z-index: 1060;
    }

    .tooltip-container[data-placement*='top'] {
        margin-bottom: 25px;
    }

    .tooltip-arrow {
        height: 1rem;
        position: absolute;
        width: 1rem;
    }

    .tooltip-arrow::after {
        border-style: solid;
        content: '';
        display: block;
        height: 0;
        margin: auto;
        position: absolute;
        width: 0;
    }

    .tooltip-arrow[data-placement*='bottom'] {
        height: 1rem;
        left: 0;
        margin-top: -0.4rem;
        top: 0;
        width: 1rem;
    }

    .tooltip-arrow[data-placement*='bottom']::after {
        border-color: transparent transparent rgba(0, 0, 0) transparent;
        border-width: 0 0.5rem 0.4rem 0.5rem;
    }

    .tooltip-arrow[data-placement*='top'] {
        bottom: 0;
        height: 1rem;
        left: 0;
        margin-bottom: -0.3rem;
        width: 1rem;
    }

    .tooltip-arrow[data-placement*='top']::after {
        border-color: rgba(0, 0, 0) transparent transparent transparent;
        border-width: 0.4rem 0.5rem 0 0.5rem;
    }

    .tooltip-arrow[data-placement*='right'] {
        height: 1rem;
        left: 0;
        margin-left: -0.7rem;
        width: 1rem;
    }

    .tooltip-arrow[data-placement*='right']::after {
        border-color: transparent rgba(0, 0, 0) transparent transparent;
        border-width: 0.5rem 0.4rem 0.5rem 0;
        left: 4px;
        top: 0;
    }

    .tooltip-arrow[data-placement*='left'] {
        height: 1rem;
        margin-right: -0.7rem;
        right: 0;
        width: 1rem;
    }

    .tooltip-arrow[data-placement*='left']::after {
        border-color: transparent transparent transparent rgba(0, 0, 0);
        border-width: 0.5rem 0 0.5rem 0.4em;
        left: -4px;
        top: 0;
    }
`;

const Container = styled('div')(({ theme }) => ({
    color: theme.color.surface,
    backgroundColor: 'rgba(0, 0, 0)',
    borderRadius: theme.borderRadius.base,
    padding: '8px 12px', // zIndex: 1060,
}));

const StyledText = styled(Text)(({ style = {} }: { style: React.CSSProperties }) => ({
    display: 'block',
    fontSize: 'medium',
    lineHeight: '1.3em',
    whiteSpace: 'pre-line',
    ...style,
}));

const defaultProps = {};

type Props = {
    title?: string | Element;
    style?: React.CSSProperties;
    textStyle?: React.CSSProperties;
    placement?: string;
    hideArrow?: boolean;
    trigger?: 'click' | 'right-click' | 'hover' | 'focus' | 'none';
};

/**
 * @example
 * ```
 * <Tooltip title={value}>
 *      Target Element
 *  </Tooltip>
 * ```
 */
const Tooltip: React.FC<Props> = ({
    children,
    title,
    textStyle,
    hideArrow,
    trigger = 'hover',
    wrapperStyles = null,
    ...props
}) => {
    return (
        <>
            <Global styles={globalStyles} />
            <TooltipTrigger
                {...props}
                trigger={!title || title === '-' ? 'none' : trigger}
                tooltip={({ arrowRef, tooltipRef, getArrowProps, getTooltipProps, placement }) => (
                    <div
                        {...getTooltipProps({
                            ref: tooltipRef,
                            className: 'tooltip-container',
                            'data-placement': placement,
                        })}
                    >
                        <Container>
                            {!hideArrow && (
                                <div
                                    {...getArrowProps({
                                        ref: arrowRef,
                                        className: 'tooltip-arrow',
                                        'data-placement': placement,
                                    })}
                                />
                            )}
                            <StyledText style={textStyle}>{title}</StyledText>
                        </Container>
                    </div>
                )}
            >
                {({ getTriggerProps, triggerRef }) => (
                    <span
                        style={wrapperStyles}
                        {...getTriggerProps({
                            ref: triggerRef,
                            className: 'trigger',
                        })}
                    >
                        {children}
                    </span>
                )}
            </TooltipTrigger>
        </>
    );
};

Tooltip.defaultProps = defaultProps;

export default Tooltip;
